import { useCallback, useMemo } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	ANALYTICS_CHANNEL,
	ANALYTICS_SOURCE,
	ANALYTICS_TAG,
	NAV_CONTAINER,
	NAV_LAYER,
	NAV_VERSION,
	type UniversalCreateSourceProduct,
} from '@atlassian/universal-create-common';
import type { PersonalizeMode } from '@atlassian/universal-create-common/bin-param';

import { useUniversalCreateState } from '../context';
import type { SourceProduct } from '../types';

// Common analytics types - extracted for reusability
export type ButtonVariant = 'standard' | 'split';
export type ButtonPart = 'primary' | 'dropdown' | 'full';
export type EntryPointLoadTrigger = 'hover' | 'controlled';

export interface MenuContentViewedAttributes {
	crossAppSuggestionsEnabled: boolean;
	nativeSections: Array<{
		itemAtis: string[];
		itemCount: number;
		sectionHeading?: string;
		sectionIndex: number;
		sectionType: string;
	}>;
	totalNativeItems: number;
	totalNativeSections: number;
}

export interface MenuItemClickedAttributes {
	ati: string;
	cloudId?: string;
	sourceProduct?: UniversalCreateSourceProduct;
	suggestionProduct?: UniversalCreateSourceProduct;
}

export interface FormButtonClickedAttributes {
	action?: string;
	actionSubjectId: string;
	itemAti: string;
	itemProduct: UniversalCreateSourceProduct;
}

export interface SuggestionMenuViewedAttributes {
	suggestedItems: string[];
	suggestionsCount: number;
}

export interface CreateButtonClickedAttributes {
	itemState: 'expanded' | 'collapsed' | 'button-only';
	version?: 'v1' | 'v2';
}

export interface CreateButtonViewedAttributes {
	buttonVariant?: ButtonVariant;
	isButtonOnly?: boolean;
	version?: 'v1' | 'v2';
}

export interface CreateButtonHoveredAttributes {
	buttonPart?: ButtonPart;
	buttonVariant?: ButtonVariant;
	itemState: 'expanded' | 'collapsed' | 'button-only';
	version?: 'v1' | 'v2';
}

export interface NativeMenuItemClickedAttributes {
	groupItemAti: string; // Atlassian Type Identifier of the item
	groupItemCount: number; // Number of items in the section
	groupItemIndex: number; // Position of the item in the section
	groupItemProduct: UniversalCreateSourceProduct; // Product item is natively associated with
	isInMore: boolean; // Whether item is in "show more" section (hidden until show more is clicked)
	isRightClick: boolean; // True if event triggered by right click opening context menu
	nativeGroupCount: number; // Total number of native sections in the menu
	nativeItemCount: number; // Total number of items in all native sections of menu
	nativeItemIndex: number; // Position of the item in the entire native sections part of menu (not just section)
}

export interface SuggestionsMenuItemClickedAttributes {
	groupItemAti: string; // Atlassian Type Identifier of the item
	groupItemCount: number; // Number of items in the section
	groupItemIndex: number; // Position of the item in the suggestions section
	groupItemProduct: UniversalCreateSourceProduct; // Product item is natively associated with
	isRightClick: boolean; // True if event triggered by right click opening context menu
}

export interface MenuItemClickedAttributes {
	ati: string; // Atlassian Type Identifier of the item
	groupItemCount: number; // Number of items in the section
	groupItemIndex: number; // Position of the item in the section
	groupItemProduct: SourceProduct; // Product item is natively associated with
	isInMore?: boolean; // Whether item is in "show more" section (hidden until show more is clicked)
	isNativeItem: boolean;
	isRightClick: boolean; // True if event triggered by right click opening context menu
	itemType: 'native' | 'integration' | 'iframe' | 'link' | 'platform';
	menuItemCount: number; // Total number of items in all sections of menu
	menuItemIndex: number; // Position of the item in the entire menu (not just section)
	sectionFoldableBehaviour?: boolean | number; // Foldable configuration of the section
	sectionType: 'appDefinedSection' | 'suggestionSection';
}

export interface NativeMenuViewedAttributes {
	crossAppSuggestionsEnabled: boolean;
	nativeGroupCount: number; // Total count of native sections in the menu
	nativeItemCount: number; // Total count of items in all native sections
	nativeMenuGroups: Array<{
		groupHeading: string;
		groupIndex: number; // Index of section out of all sections
		groupItemAtisList: string[];
		groupItemCount: number; // total number of items in the section
		inMoreItemAtisList: string[];
		inMoreItemCount: number;
		showMoreButton: boolean; // true if items in section have been truncated with show more button
	}>;
}

export interface MenuViewedAttributes {
	crossAppCreationEnabled: boolean;
	groupCount: number; // total number of sections in the menu
	groupList: Array<
		{
			groupHeading?: string;
			groupIndex: number; // Index of section out of all sections
			groupItemAtisList: string[];
			groupItemCount: number; // total number of items in the section
			inMoreItemAtisList?: string[]; // Only present if showMoreButton is true
			inMoreItemCount?: number; // Only present if showMoreButton is true
			itemTypes: string[];
			showMoreButton: boolean; // true if items in section have been truncated with show more button
		} & (
			| {
					personalize: PersonalizeMode;
					type: 'appDefinedSection';
			  }
			| { type: 'suggestionSection' }
		)
	>;
	recommendationSessionId?: string;
	totalCrossAppItemCount: number;
	totalNativeItemCount: number;
}

export interface SuggestionsMenuViewedAttributes {
	crossAppSuggestionsEnabled: boolean;
	suggestionGroupList: Array<{
		groupHeading: string;
		groupIndex: number; // Index of section out of all sections
		groupItemAtisList: string[];
		groupItemCount: number; // total number of items in the section
		inMoreItemAtisList?: string[]; // list of atis of items that will become visible after expansion
		inMoreItemCount?: number;
		showMoreButton?: boolean; // true if items in section have been truncated with show more button
	}>;
	suggestionsGroupCount: number; // Total count of suggestion sections in the menu
	suggestionsItemCount: number; // Total count of items in suggestions section
}

export interface ShowMoreButtonClickedAttributes {
	groupHeading: string;
	groupIndex: number; // Index of section out of all sections
	groupItemCount: number; // total number of items in the section
	inMoreItemAtisList: string[]; // list of atis of items that will become visible after expansion
	inMoreItemCount: number; // Count of items originally hidden by show more button truncation
}

export interface EntryPointLoadTriggeredAttributes {
	trigger: EntryPointLoadTrigger;
	version?: 'v1' | 'v2';
}

export interface JSLoadTimeAttributes {
	durationInMs: number;
	/**
	 * @default false
	 */
	hasError?: boolean;
}

export interface UniversalCreateAnalyticsEvents {
	// Create button click events
	fireCreateButtonClicked: (attributes: CreateButtonClickedAttributes) => void;
	fireCreateButtonHovered: (attributes: CreateButtonHoveredAttributes) => void;
	fireCreateButtonViewed: (attributes: CreateButtonViewedAttributes) => void;

	// Entry point load events
	fireEntryPointLoadTriggered: (attributes: EntryPointLoadTriggeredAttributes) => void;

	// Modal control analytic events
	fireFormControlAttributes: (attributes: FormButtonClickedAttributes) => void;

	// JS chunk load time events
	fireJSChunkLoadTime: (attributes: JSLoadTimeAttributes) => void;

	// Menu content V2 unmount
	fireMenuContentClosed: () => void;

	// Combined Menu item click events
	fireMenuItemClicked: (attributes: MenuItemClickedAttributes) => void;

	// Menu items viewed events
	fireMenuItemsViewed: (attributes: MenuViewedAttributes) => void;

	// Universal create menu expanded or collapsed via any exit action
	fireMenuStateChanged: (attributes: CreateButtonClickedAttributes) => void;

	// Native Menu item click events
	fireNativeMenuItemClicked: (attributes: NativeMenuItemClickedAttributes) => void;

	// Native Menu items viewed events
	fireNativeMenuViewed: (attributes: NativeMenuViewedAttributes) => void;

	// Show more button clicked events
	fireShowMoreButtonClicked: (attributes: ShowMoreButtonClickedAttributes) => void;

	// Suggestions Menu item click events
	fireSuggestionsMenuItemClicked: (attributes: SuggestionsMenuItemClickedAttributes) => void;

	// Suggestions Menu items viewed events
	fireSuggestionsMenuViewed: (attributes: SuggestionsMenuViewedAttributes) => void;
}

/**
 * Custom hook for Universal Create analytics events
 * Provides type-safe, consistent analytics event firing across all UC components
 */
export function useUniversalCreateAnalytics(): UniversalCreateAnalyticsEvents {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { growthRecommendationsSessionId, product, subProduct } = useUniversalCreateState();

	// Provide fallback for product if undefined
	const safeProduct = product || 'unknown';

	const includeGrowthSessionInAnalytics = fg('navx-3810-re-instrument-growth-session-id');

	const baseAttributes = useMemo(
		() => ({
			navLayer: NAV_LAYER,
			navContainer: NAV_CONTAINER,
			navVersion: NAV_VERSION,
			product: safeProduct,
			subProduct,
			...(includeGrowthSessionInAnalytics &&
				growthRecommendationsSessionId !== undefined && {
					recommendationSessionId: growthRecommendationsSessionId,
				}),
		}),
		[growthRecommendationsSessionId, includeGrowthSessionInAnalytics, safeProduct, subProduct],
	);

	const topLevelAttributes = useMemo(
		() => ({
			actionSubject: 'universalCreate',
			eventType: 'ui',
			source: ANALYTICS_SOURCE,
			tags: [ANALYTICS_TAG],
		}),
		[],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/88038
	const fireCreateButtonClicked = useCallback(
		(attributes: CreateButtonClickedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				action: 'clicked',
				actionSubjectId: 'universalCreateButton',
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	const fireCreateButtonViewed = useCallback(
		(attributes: CreateButtonViewedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				action: 'viewed',
				actionSubjectId: 'universalCreateButton',
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	const fireCreateButtonHovered = useCallback(
		(attributes: CreateButtonHoveredAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				action: 'hovered',
				actionSubjectId: 'universalCreateButton',
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	const fireEntryPointLoadTriggered = useCallback(
		(attributes: EntryPointLoadTriggeredAttributes) => {
			createAnalyticsEvent({
				actionSubject: 'universalCreateEntryPointLoad',
				action: 'triggered',
				eventType: 'track',
				source: ANALYTICS_SOURCE,
				tags: [ANALYTICS_TAG],
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/88332
	const fireNativeMenuItemClicked = useCallback(
		(attributes: NativeMenuItemClickedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				action: 'clicked',
				actionSubjectId: 'universalCreateNativeMenuItem',
				attributes: {
					group: 'native',
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/88486
	const fireSuggestionsMenuItemClicked = useCallback(
		(attributes: SuggestionsMenuItemClickedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				action: 'clicked',
				actionSubjectId: 'universalCreateSuggestionsMenuItem',
				attributes: {
					group: 'suggestions',
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/88295
	const fireMenuItemClicked = useCallback(
		(attributes: MenuItemClickedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				action: 'clicked',
				actionSubjectId: 'universalCreateMenuItem',
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/88599
	const fireNativeMenuViewed = useCallback(
		(attributes: NativeMenuViewedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				action: 'viewed',
				actionSubjectId: 'universalCreateNativeMenu',
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/88724
	const fireSuggestionsMenuViewed = useCallback(
		(attributes: SuggestionsMenuViewedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				action: 'viewed',
				actionSubjectId: 'universalCreateSuggestionsMenu',
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/92169
	const fireMenuItemsViewed = useCallback(
		(attributes: MenuViewedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				action: 'viewed',
				actionSubjectId: 'universalCreateMenuSection',
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/100116
	const fireMenuContentClosed = useCallback(() => {
		createAnalyticsEvent({
			...topLevelAttributes,
			action: 'closed',
			actionSubjectId: 'universalCreateMenuContent',
			attributes: {
				...baseAttributes,
			},
		}).fire(ANALYTICS_CHANNEL);
	}, [createAnalyticsEvent, baseAttributes, topLevelAttributes]);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/89698
	const fireMenuStateChanged = useCallback(
		(attributes: CreateButtonClickedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				action: 'changed',
				actionSubjectId: 'universalCreateMenuState',
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, topLevelAttributes, baseAttributes],
	);

	// createModal viewed Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/90539
	// createModalBackButton clicked Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/90540
	// createModalCancelButton clicked Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/90541
	// createModalCreateButton clicked Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/90542
	const fireFormControlAttributes = useCallback(
		({ actionSubjectId, action = 'clicked', ...attributes }: FormButtonClickedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				actionSubject: 'universalCreate',
				action,
				actionSubjectId,
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/92730
	const fireShowMoreButtonClicked = useCallback(
		(attributes: ShowMoreButtonClickedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				action: 'clicked',
				actionSubjectId: 'universalCreateShowMoreButton',
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/96626
	const fireJSChunkLoadTime = useCallback(
		(attributes: JSLoadTimeAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				action: 'loaded',
				actionSubject: 'universalCreateJSChunkLoad',
				actionSubjectId: 'universalCreateJSChunkLoad',
				eventType: 'operational',
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	return {
		fireCreateButtonClicked,
		fireCreateButtonHovered,
		fireCreateButtonViewed,
		fireEntryPointLoadTriggered,
		fireNativeMenuItemClicked, // merging
		fireSuggestionsMenuItemClicked, // merging
		fireMenuItemClicked, // new ==> (merging) combining native and suggestions
		fireNativeMenuViewed, // new + updated
		fireSuggestionsMenuViewed, // new + updated
		fireMenuItemsViewed,
		fireMenuContentClosed,
		fireMenuStateChanged,
		fireFormControlAttributes,
		fireShowMoreButtonClicked,
		fireJSChunkLoadTime,
	};
}
